import apiClient from "@shared/services/apiClient";

export type AiTriageCloseDraftResult = {
  success: boolean;
  report?: {
    report_id?: string;
    status?: string;
    is_signed?: boolean;
  };
  studyId?: string;
  status?: "draft";
  isSigned?: boolean;
  canSignViaAi?: boolean;
  confirmedEventCount?: number;
  confirmedEventIds?: string[];
  pendingOrBlockingCount?: number;
  attendingUserIds?: string[];
  attendingNotified?: boolean;
  attendingUnresolved?: boolean;
  message?: string;
  error?: string;
};

const inFlight = new Map<string, Promise<AiTriageCloseDraftResult>>();
const recent = new Map<
  string,
  { result: AiTriageCloseDraftResult; at: number }
>();
const RECENT_TTL_MS = 120_000;

export function triageCloseSessionKey(
  studyId?: string | null,
  recordingId?: string | null,
  notifyAttending = true,
): string {
  return `${studyId || ""}:${recordingId || ""}:notify=${notifyAttending ? "1" : "0"}`;
}

export async function completeAiTriageCloseDraft(input: {
  studyId?: string | null;
  recordingId?: string | null;
  attendingUserId?: string | null;
  notifyAttending?: boolean;
  force?: boolean;
}): Promise<AiTriageCloseDraftResult> {
  if (!input.studyId && !input.recordingId) {
    return {
      success: false,
      error: "studyId or recordingId is required to close triage",
    };
  }
  const notifyAttending = input.notifyAttending !== false;
  const key = triageCloseSessionKey(
    input.studyId,
    input.recordingId,
    notifyAttending,
  );
  if (!input.force) {
    const cached = recent.get(key);
    if (cached && Date.now() - cached.at < RECENT_TTL_MS) {
      return cached.result;
    }
  }
  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = (async () => {
    const response = await apiClient.post(
      "/llm/triage-close-draft",
      {
        studyId: input.studyId || undefined,
        recordingId: input.recordingId || undefined,
        attendingUserId: input.attendingUserId || undefined,
        notifyAttending,
      },
      { timeout: 120000 },
    );
    const payload = (response.data || {}) as AiTriageCloseDraftResult;
    if (
      payload.isSigned ||
      payload.report?.is_signed ||
      payload.report?.status === "signed"
    ) {
      throw new Error("Server returned a signed report from triage close");
    }
    const result = {
      ...payload,
      success: payload.success !== false,
      status: "draft" as const,
      isSigned: false,
      canSignViaAi: false,
      attendingNotified: notifyAttending
        ? Boolean(payload.attendingNotified)
        : false,
    };
    recent.set(key, { result, at: Date.now() });
    return result;
  })();

  inFlight.set(key, request);
  try {
    return await request;
  } finally {
    inFlight.delete(key);
  }
}

export default completeAiTriageCloseDraft;
